export type ToggleInfo = 'icon' | 'list';

/** 스트리밍 중 노출되는 도구 호출 상태 (BotMessage 의 chip 으로 렌더). */
export interface ToolCallInfo {
  name: string;
  args?: Record<string, unknown>;
  status: 'running' | 'ok' | 'error';
  latencyMs?: number;
  error?: string;
}

export interface Message {
  /** 클라이언트 측 안정 고유 키 (v-for :key 용). addMessage 시 1회 부여 — 윈도우 슬라이싱/메시지
   * 추가에도 불변이라 DOM 노드 오재사용(차트/lottie/펼침 상태 오염)을 방지. */
  id?: string;
  messageType: string;
  message: string;
  sender: string;
  resultType: string;
  result: string;
  imageUrl: string;
  /** 스트리밍 진행 중 (true 면 BotMessage 에 typing cursor + tool chips 렌더). */
  isPartial?: boolean;
  /** 누적된 도구 호출 진행 상태. SSE tool_call/tool_result 이벤트로 추가. */
  toolCalls?: ToolCallInfo[];
  /** 현재 단계 — 'thinking' 또는 'tool'. SSE 'status' 이벤트로 갱신, partial 일 때만 의미.
   *  chip 시스템(streamToolEvents)과 무관한 가벼운 overall 진행 indicator. */
  statusKind?: 'thinking' | 'tool';
  /** statusKind==='tool' 일 때의 도구 이름 (예: 'get_system_health'). 라벨 변환은 TOOL_LABELS. */
  statusToolName?: string;
  /** AnalysisButton 으로 생성된 메시지가 참조하는 위젯의 출처 URL. UserMessage 가 imageUrl
   * 썸네일을 렌더할 때 click → router.push(sourcePath) 로 해당 차트 화면으로 이동. */
  sourcePath?: string;
  /** 위젯 제목 — UserMessage 의 caption 으로 사용. */
  sourceTitle?: string;
  /** 봇 응답 메타 JSON — {provider, model, inputTokens, outputTokens, totalTokens, elapsedMs}.
   * 표시 여부는 환경설정 responseMetaOptions 로 항목별 on/off (BotMessage 가 필터). */
  metaJson?: string;
  /** 인라인 차트 배열 JSON — 리포트 chart 섹션 스키마 동일 (백엔드가 추이/Top-N 도구 결과에서 자동 첨부). */
  chartsJson?: string;
  /** 알람 임계치 추천 배열 JSON — recommend_alarm_thresholds 결과(인스턴스별). AlarmRecommendTable 이 렌더. */
  alarmRecsJson?: string;
  /** 구조화 응답 카드 JSON — present_structured_answer 결과. StructuredAnswer.vue 가 고정 레이아웃으로 렌더. */
  structuredAnswerJson?: string;
  /** 메시지 주고받은 시각 (ISO-8601 UTC). 서버 히스토리는 DB 저장 시각, 신규 메시지는 addMessage 가
   * client now 부여. 메시지별 시각 표시(HH:mm)에 사용. */
  createdAt?: string;
}

/** present_structured_answer 도구 결과 — 분석/RCA 답변의 고정 구조화 카드. 백엔드 출력 스키마와 1:1. */
export interface StructuredAnswer {
  verdict: string;
  severity?: string;
  summary?: Array<{ label: string; value: string }>;
  causes?: Array<{ cause: string; evidence: string }>;
  actions?: string[];
}

/** 연계 조사 카드(resultType 'investigation')의 추적 경로 노드 1개. 백엔드 출력 스키마와 1:1. */
export interface InvestigationTargetRef {
  name: string;
  targetId: string;
  targetKind: string;
  subKind?: string;
}

/** 연계 조사 카드의 후속 분석 액션 버튼 — message 를 suggestion 흐름으로 전송. */
export interface InvestigationAction {
  label: string;
  message: string;
}

/** 연계 조사 카드 — 확장 조사 배너 + 추적 경로 체인(source → related) + 연계 분석 액션. */
export interface InvestigationPath {
  banner: string;
  source: InvestigationTargetRef;
  related: InvestigationTargetRef[];
  actions?: InvestigationAction[];
}

/** recommend_alarm_thresholds 도구 결과 1건(인스턴스별). 백엔드 출력 스키마와 1:1. */
export interface AlarmRecommendation {
  target_id: string;
  instance_name: string;
  sub_kind: string;
  baseline_days: number;
  formula?: string;
  metric_count?: number;
  metrics_with_data?: number;
  metrics: AlarmRecommendMetric[];
}

export interface AlarmRecommendMetric {
  data_id: string;
  name: string;
  unit: string;
  direction: 'higher_worse' | 'lower_worse';
  windows?: Record<string, { avg: number | null; max: number | null; samples: number } | null>;
  baseline?: {
    window: string;
    samples: number;
    avg: number | null;
    p95: number | null;
    p99: number | null;
    p05: number | null;
    max: number | null;
  } | null;
  recommend?: {
    warning: number | null;
    critical: number | null;
    warningComparison: string;
    criticalComparison: string;
  } | null;
  recommend_note?: string;
  no_data?: boolean;
  apply: {
    /** 메트릭 stat 카테고리 (지표 선택용). 공통 WAS 지표는 targetCategory 와 다를 수 있음. */
    category: string;
    /** 대상 태그 바인딩 카테고리 (groupTagId). DB/WAS sub_kind, kafka target_kind. */
    targetCategory?: string;
    dataId: string;
    targetId: string;
    targetName: string;
  };
}

export interface Position {
  top: number;
  left: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface ScrollPosition {
  scrollTop: number;
}

export interface ChatState {
  button: Position;
  window: Position;
  windowScroll: ScrollPosition;
  windowSize: { width: number; height: number };
}

export interface TargetData {
  targetKind: string;
  targetName: string;
  targetAlertLevel: string;
  targetId: string;
}

export type TriggerPathType =
  | 'wasInfo'
  | 'podInfo'
  | 'namespaceInfo'
  | 'nodeInfo'
  | 'hpaInfo'
  | 'pvcInfo'
  | 'dbInfo'
  | 'hostInfo'
  | 'processInfo'
  | 'containerInfo'
  | 'networkDeviceInfo'
  | 'networkObjectInfo'
  | 'networkInterface'
  | 'kafkaClusterInfo'
  | 'kafkaBrokerInfo'
  | 'kafkaTopicInfo'
  | 'awsInfo'
  | 'azureInfo'
  | 'serviceInfo'
  | 'sqlInfo'
  | 'sessionInfo'
  | 'activeSessionInfo'
  | 'activeTxnList'
  | 'cubridBrokerInfo'
  | 'alertInfo'
  | 'urlMonitoringInfo'
  | 'statefulSetInfo'
  | 'clusterRolesInfo'
  | 'clusterRolesBindingsInfo'
  | 'serviceAccountsInfo'
  | 'replicaSetInfo'
  | 'configMapInfo'
  | 'roleBindingInfo'
  | 'deploymentInfo'
  | 'daemonSetInfo'
  | 'cronJobInfo'
  | 'clusterInfo'
  | 'jobInfo'
  | 'webserverInfo'
  | 'processDetailInfo'
  | 'openshiftRouteInfo'
  | 'serviceDetailInfo'
  | 'gpuInfo';

export const CATEGORIES = [
  'business_service',
  'application',
  'host',
  'host_port',
  'gpu',
  'mig',
  'statefulset',
  'service',
  'replicaset',
  'pod',
  'node',
  'network_device',
  'namespace',
  'kafka_cluster',
  'kafka_zookeeper',
  'kafka_broker',
  'kafka_broker_topic',
  'kafka_cluster_topic',
  'job',
  'interface',
  'filesystem',
  'deployment',
  'database',
  'daemonset',
  'cronjob',
  'container',
  'cluster',
  'cloud',
  'aws_account',
  'aws_aurora',
  'aws_dynamodb',
  'aws_redshift',
  'aws_documentdb',
  'aws_elasticache_cluster',
  'aws_elasticache_node',
  'aws_elasticache_serverless',
  'aws_elasticache_shard',
  'aws_ec2',
  'aws_lambda',
  'aws_lightsail_database',
  'aws_lightsail_instance',
  'aws_rds',
  'aws_s3',
  'aws_vpc',
  'aws_dms_replinstance',
  'aws_dms_task',
  'azure_virtual_machines',
  'azure_functions',
  'azure_blob_storage',
  'azure_cosmos_db',
  'azure_cache_for_redis',
  'azure_cache_for_redis_enterprise',
  'azure_sql_database',
  'azure_database_for_mysql',
  'azure_database_for_postgresql',
  'oracle',
  'postgresql',
  'mysql',
  'redis',
  'sqlserver',
  'java',
  'dotnet',
  'python',
  'nodejs',
  'process',
  'mongodb',
  'tibero',
  'cubrid',
  'clickhouse',
  'database_cluster',
  'configmap',
  'hpa',
  'persistent_volume',
  'persistent_volume_claim',
  'storage_class',
  'ingress',
  'role',
  'role_binding',
  'cluster_role',
  'cluster_role_binding',
  'service_account',
  'network_object',
  'url',
  'webserver',
  'altibase',
  'processDetailInfo',
  'route',
  'rum',
] as const;

export type Category = (typeof CATEGORIES)[number];

export const isCategory = (value: string): value is Category =>
  (CATEGORIES as readonly string[]).includes(value);
